import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Button, Card, Checkbox, Input } from '@/components/ui'
import { ModuleSettings } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getSettingsModules,
  updateSettingsModules,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'

interface IModuleValues {
  artistsEnabled: boolean
  homepageArtistsLimit: number
  newsletterEnabled: boolean
  newsEnabled: boolean
  homepageNewsLimit: number
  stagesEnabled: boolean
  appPromoEnabled: boolean
  videoGalleryEnabled: boolean
  instagramPostsLimit: number
  friendsEnabled: boolean
  memoriesEnabled: boolean
  instagramWidgetEnabled: boolean
  instagramAppId?: string
  instagramAppToken?: string
  sendAsGiftEnabled?: boolean
  referralEnabled: boolean
}

export const ModuleManagement: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()
  const [modules, setModules] = useState<ModuleSettings>()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsModules()
        if (resp.status === 200) {
          setModules(resp.data)
          setValue('artistsEnabled', resp.data.artistsEnabled)
          setValue('homepageArtistsLimit', resp.data.homepageArtistsLimit)
          setValue('newsletterEnabled', resp.data.newsletterEnabled)
          setValue('newsEnabled', resp.data.newsEnabled)
          if (resp.data.homepageNewsLimit) {
            setValue('homepageNewsLimit', resp.data.homepageNewsLimit)
          } else {
            resetField("homepageNewsLimit")
          }
          setValue('stagesEnabled', resp.data.stagesEnabled)
          setValue('appPromoEnabled', resp.data.appPromoEnabled)
          setValue('videoGalleryEnabled', resp.data.videoGalleryEnabled)
          if (resp.data.instagramPostsLimit) {
            setValue('instagramPostsLimit', resp.data.instagramPostsLimit)
          } else {
            resetField("instagramPostsLimit")
          }
          setValue('friendsEnabled', resp.data.friendsEnabled)
          setValue('memoriesEnabled', resp.data.memoriesEnabled)
          setValue('instagramWidgetEnabled', resp.data.instagramWidgetEnabled)
          setValue('referralEnabled', resp.data.referralEnabled)
          if (resp.data.instagramAppId) {
            setValue('instagramAppId', resp.data.instagramAppId)
          } else {
            resetField('instagramAppId')
          }
          if (resp.data.instagramAppToken) {
            setValue('instagramAppToken', resp.data.instagramAppToken)
          } else {
            resetField("instagramAppToken")
          }
          setValue('sendAsGiftEnabled', resp.data.sendAsGiftEnabled)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    artistsEnabled: yup.boolean().required('Field is required'),
    homepageArtistsLimit: yup.number().required('Field is required'),
    newsletterEnabled: yup.boolean().required('Field is required'),
    newsEnabled: yup.boolean().required('Field is required'),
    homepageNewsLimit: yup.number().required('Field is required'),
    stagesEnabled: yup.boolean().required('Field is required'),
    appPromoEnabled: yup.boolean().required('Field is required'),
    videoGalleryEnabled: yup.boolean().required('Field is required'),
    instagramPostsLimit: yup.number().required('Field is required'),
    friendsEnabled: yup.boolean().required('Field is required'),
    memoriesEnabled: yup.boolean().required('Field is required'),
    instagramWidgetEnabled: yup.boolean().required('Field is required'),
    instagramAppId: yup.string().optional(),
    instagramAppToken: yup.string().optional(),
    sendAsGiftEnabled: yup.boolean().optional(),
    referralEnabled: yup.boolean().required('Field is required'),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    resetField,
    control,
  } = useForm<IModuleValues>({
    mode: 'onChange',
    resolver: yupResolver<IModuleValues>(schema),
    defaultValues: {},
  })

  const submit = async (values: IModuleValues) => {
    try {
      const { status } = await updateSettingsModules(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (modules) {
      setValue('artistsEnabled', modules.artistsEnabled)
      setValue('homepageArtistsLimit', modules.homepageArtistsLimit)
      setValue('newsletterEnabled', modules.newsletterEnabled)
      setValue('newsEnabled', modules.newsEnabled)
      setValue('homepageNewsLimit', modules.homepageNewsLimit)
      setValue('stagesEnabled', modules.stagesEnabled)
      setValue('appPromoEnabled', modules.appPromoEnabled)
      setValue('videoGalleryEnabled', modules.videoGalleryEnabled)
      setValue('instagramPostsLimit', modules.instagramPostsLimit)
      setValue('friendsEnabled', modules.friendsEnabled)
      setValue('memoriesEnabled', modules.memoriesEnabled)
      setValue('instagramWidgetEnabled', modules.instagramWidgetEnabled)
      setValue('referralEnabled', modules.referralEnabled)
      if (modules.instagramAppId) {
        setValue('instagramAppId', modules.instagramAppId)
      }
      if (modules.instagramAppToken) {
        setValue('instagramAppToken', modules.instagramAppToken)
      }
      setValue('sendAsGiftEnabled', modules.sendAsGiftEnabled)
    }
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-module-management-form"
          className="space-y-7"
          onSubmit={handleSubmit(submit)}
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">
            Module Management
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-4 bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
              <FormGroup
                label="Artists"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'artistsEnabled',
                }}
              >
                <Controller
                  name="artistsEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="artistsEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup label="Number of Artists to Display on Homepage">
                <Input
                  isInvalid={!!errors?.homepageArtistsLimit?.message}
                  {...register('homepageArtistsLimit')}
                  type="number"
                  placeholder="Enter Number of Artists"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
            </div>
            <div className="space-y-4 bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
              <FormGroup
                label="News"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'newsEnabled',
                }}
              >
                <Controller
                  name="newsEnabled"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="newsEnabled"
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={!hasEditPermission}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup label="Number of News to Display on Homepage">
                <Input
                  isInvalid={!!errors?.homepageNewsLimit?.message}
                  {...register('homepageNewsLimit')}
                  type="number"
                  placeholder="Enter Number of News"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 divide-x divide-monochrome-120">
            <FormGroup
              label="Newsletter"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'newsletterEnabled',
              }}
            >
              <Controller
                name="newsletterEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="newsletterEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              label="Stages"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'stagesEnabled',
              }}
            >
              <Controller
                name="stagesEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="stagesEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="App Promo"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'appPromoEnabled',
              }}
            >
              <Controller
                name="appPromoEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="appPromoEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Video Gallery"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'videoGalleryEnabled',
              }}
            >
              <Controller
                name="videoGalleryEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="videoGalleryEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
          </div>
          <div className="grid grid-cols-4 gap-4 divide-x divide-monochrome-120">
            <FormGroup
              label="Memories"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'memoriesEnabled',
              }}
            >
              <Controller
                name="memoriesEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="memoriesEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Our Friends Widget"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'friendsEnabled',
              }}
            >
              <Controller
                name="friendsEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="friendsEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              label="Referral"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'referralEnabled',
              }}
            >
              <Controller
                name="referralEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="referralEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Send As Gift"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'sendAsGiftEnabled',
              }}
            >
              <Controller
                name="sendAsGiftEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="sendAsGiftEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
          </div>

          <div className="bg-monochrome-3-60 border border-monochrome-120 rounded-2xl p-4">
            <FormGroup
              label="Instagram Widget"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'instagramWidgetEnabled',
              }}
            >
              <Controller
                name="instagramWidgetEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="instagramWidgetEnabled"
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={!hasEditPermission}
                  />
                )}
              />
            </FormGroup>
            <div className="grid grid-cols-3 gap-4 mt-5">
              <FormGroup label="Enter Instagram App ID">
                <Input
                  isInvalid={!!errors?.instagramAppId?.message}
                  {...register('instagramAppId')}
                  type="text"
                  placeholder="Enter Instagram App ID"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="Enter Instagram App Token">
                <Input
                  isInvalid={!!errors?.instagramAppToken?.message}
                  {...register('instagramAppToken')}
                  type="text"
                  placeholder="Enter Instagram App Token"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="Number Posts to Display">
                <Input
                  isInvalid={!!errors?.instagramPostsLimit?.message}
                  {...register('instagramPostsLimit')}
                  type="number"
                  placeholder="Enter Number Posts to Display"
                  disabled={!hasEditPermission}
                />
              </FormGroup>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-module-management-form"
            type="submit"
            variant="primary"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
